import { useState } from "react";
import { STATE_ICONS } from "@/components/ui/concept-icons";
import { Button } from "@/components/ui";
import { MarkdownContent } from "@/components/ui/markdown-content";
import { useT } from "@/i18n";
import { framesConversationMessages } from "@/i18n/messages/frames-conversation";
import { openExternalUrl } from "@/lib/open-external-url";
import { openTaskDetails } from "@/lib/right-panel-navigation";
import { useTasksStore } from "@/stores/tasks-store";
import type { DirectSuggestion } from "@/lib/direct-result-notice";

export function ConversationDirectResultNotice({
  id,
  title,
  taskId,
  projectId,
  content,
  suggestions,
  onSuggestionSend,
  testId = "conversation-frame",
}: {
  id: string;
  title: string;
  taskId: string | null;
  projectId?: string;
  content: string;
  suggestions: DirectSuggestion[];
  onSuggestionSend?: (text: string) => void;
  testId?: string;
}) {
  const t = useT(framesConversationMessages);
  const noticeTestId = `${testId}-direct-result-${id}`;
  const changeTaskStatus = useTasksStore((s) => s.changeTaskStatus);
  const storedStatus = useTasksStore((s) =>
    taskId ? s.tasks.find((task) => task.id === taskId)?.status : undefined,
  );
  const [openError, setOpenError] = useState("");
  const [markingDone, setMarkingDone] = useState(false);
  const [markedDone, setMarkedDone] = useState(false);
  const taskDone = storedStatus === "done" || (markedDone && !storedStatus);

  async function markDone(project: string, task: string) {
    setMarkingDone(true);
    const changed = await changeTaskStatus(project, task, "done");
    setMarkingDone(false);
    if (changed) setMarkedDone(true);
  }

  async function openInApp(url: string) {
    setOpenError("");
    try {
      await openExternalUrl(url);
    } catch {
      setOpenError(t("openAppFailed"));
    }
  }

  function runSuggestion(suggestion: DirectSuggestion) {
    if (suggestion.kind === "send" && suggestion.message) {
      onSuggestionSend?.(suggestion.message);
    } else if (suggestion.kind === "open_app" && suggestion.url) {
      void openInApp(suggestion.url);
    } else if (suggestion.kind === "mark_done" && taskId && projectId) {
      void markDone(projectId, taskId);
    }
  }

  function suggestionEnabled(suggestion: DirectSuggestion) {
    if (suggestion.kind === "send") return onSuggestionSend !== undefined;
    if (suggestion.kind === "open_app") return true;
    return taskId !== null && projectId !== undefined && !markingDone;
  }

  return (
    <div
      className="my-2 rounded-control border border-glass-stroke-faint bg-glass-fill px-3 py-2"
      data-testid={noticeTestId}
    >
      <div className="flex items-center gap-2">
        <STATE_ICONS.done className="h-3.5 w-3.5 shrink-0 text-accent" />
        <span className="min-w-0 flex-1 truncate text-canvas-xs font-semibold text-primary">
          {title}
        </span>
        {taskId && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => void openTaskDetails(taskId)}
            data-testid={`${noticeTestId}-open-task`}
          >
            {t("openTask")}
          </Button>
        )}
      </div>
      <MarkdownContent
        content={content}
        className="mt-1 line-clamp-6 text-canvas-xs text-secondary [&_p]:leading-snug"
      />
      {suggestions.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {suggestions.map((suggestion, index) =>
            suggestion.kind === "mark_done" && taskDone ? (
              <span
                key={`${suggestion.kind}-${index}`}
                className="inline-flex items-center gap-1 px-2 text-canvas-xs text-secondary"
                data-testid={`${noticeTestId}-task-done`}
              >
                <STATE_ICONS.done className="h-3 w-3 text-accent" />
                {t("taskDone")}
              </span>
            ) : (
              <Button
                key={`${suggestion.kind}-${index}`}
                type="button"
                variant="secondary"
                size="sm"
                disabled={!suggestionEnabled(suggestion)}
                onClick={() => runSuggestion(suggestion)}
                data-testid={`${noticeTestId}-suggestion-${suggestion.kind}`}
              >
                {suggestion.label}
              </Button>
            ),
          )}
        </div>
      )}
      {openError && (
        <p
          role="alert"
          className="mt-2 text-canvas-xs text-error"
          data-testid={`${noticeTestId}-open-error`}
        >
          {openError}
        </p>
      )}
    </div>
  );
}
